import { getDbSupabase, supabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { DemCloudTalkAgentLive } from '../types/dem';

export type CloudTalkCredentials = {
  apiId: string;
  apiSecret: string;
};

const CLOUDTALK_API_BASE =
  import.meta.env.VITE_CLOUDTALK_API_BASE?.trim() || '/cloudtalk-api';

const lastAgentStatus = new Map<string, string>();

export function isCloudTalkPollingConfigured(): boolean {
  const apiId = import.meta.env.VITE_CT_API_ID?.trim() ?? '';
  const apiSecret = import.meta.env.VITE_CT_API_SECRET?.trim() ?? '';
  return Boolean(apiId && apiSecret);
}

export function getCloudTalkCredentials(): CloudTalkCredentials | null {
  const apiId = import.meta.env.VITE_CT_API_ID?.trim() ?? '';
  const apiSecret = import.meta.env.VITE_CT_API_SECRET?.trim() ?? '';
  if (!apiId || !apiSecret) return null;
  return { apiId, apiSecret };
}

function basicAuthHeader(creds: CloudTalkCredentials): string {
  return `Basic ${btoa(`${creds.apiId}:${creds.apiSecret}`)}`;
}

async function parseCloudTalkJson(response: Response, label: string): Promise<unknown> {
  const text = await response.text();
  const trimmed = text.trim();

  if (!response.ok) {
    if (trimmed.startsWith('<')) {
      throw new Error(
        `${label} returned HTML (HTTP ${response.status}). Configure the /cloudtalk-api proxy on the portal server (see cloudtalk-proxy.php).`,
      );
    }
    throw new Error(`${label} returned HTTP ${response.status}`);
  }

  if (trimmed.startsWith('<') || trimmed.startsWith('<!DOCTYPE')) {
    throw new Error(
      `${label} returned a web page instead of JSON. Upload cloudtalk-proxy.php and update .htaccess on the portal server.`,
    );
  }

  // DELETE / some edits may return an empty body with 2xx.
  if (!trimmed) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error(`${label} returned invalid JSON. Check that /cloudtalk-api is configured correctly.`);
  }
}

export async function cloudTalkFetch(
  creds: CloudTalkCredentials,
  path: string,
  timeoutMs: number,
  accept = 'application/json',
  options?: {
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    body?: unknown;
  },
): Promise<Response> {
  const method = options?.method ?? 'GET';
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers: Record<string, string> = {
      Authorization: basicAuthHeader(creds),
      Accept: accept,
    };
    let body: string | undefined;
    if (method === 'PUT' || method === 'POST') {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options?.body ?? {});
    }
    return await fetch(`${CLOUDTALK_API_BASE}${path}`, {
      method,
      headers,
      body,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
}

export async function cloudTalkFetchJson(
  creds: CloudTalkCredentials,
  path: string,
  label: string,
  timeoutMs = 60_000,
  options?: {
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    body?: unknown;
  },
): Promise<unknown> {
  const response = await cloudTalkFetch(creds, path, timeoutMs, 'application/json', options);
  return parseCloudTalkJson(response, label);
}

/**
 * Fetch call recording WAV from CloudTalk (not stored in Supabase).
 * Caller must already enforce Management / IT access in the UI.
 */
export async function fetchCloudTalkRecordingBlob(callId: string): Promise<Blob> {
  const id = String(callId || '').trim();
  if (!/^\d+$/.test(id)) {
    throw new Error('Invalid CloudTalk call id for recording.');
  }
  const creds = getCloudTalkCredentials();
  if (!creds) {
    throw new Error('CloudTalk API is not configured (missing VITE_CT_API_ID / VITE_CT_API_SECRET).');
  }

  const response = await cloudTalkFetch(
    creds,
    `/calls/recording/${id}.json`,
    120_000,
    'audio/wav, audio/x-wav, application/octet-stream, */*',
  );

  if (response.status === 404) {
    throw new Error('No recording available for this call.');
  }
  if (response.status === 410) {
    throw new Error('Recording has expired on CloudTalk.');
  }
  if (!response.ok) {
    throw new Error(`Recording fetch failed (HTTP ${response.status}).`);
  }

  const buffer = await response.arrayBuffer();
  if (!buffer.byteLength) {
    throw new Error('Empty recording response.');
  }

  const bytes = new Uint8Array(buffer);
  // JSON error bodies start with `{`; HTML with `<`.
  if (bytes[0] === 0x7b || bytes[0] === 0x3c) {
    const text = new TextDecoder().decode(buffer);
    if (bytes[0] === 0x7b) {
      try {
        const json = JSON.parse(text) as {
          responseData?: { message?: string };
          error?: string;
          message?: string;
        };
        throw new Error(
          json.responseData?.message || json.error || json.message || 'No recording available.',
        );
      } catch (e) {
        if (!(e instanceof SyntaxError)) throw e;
      }
    }
    throw new Error('No recording available for this call.');
  }

  const upstreamType = response.headers.get('Content-Type') || '';
  const mime =
    upstreamType.includes('audio') || upstreamType.includes('wav')
      ? upstreamType.split(';')[0]!.trim()
      : 'audio/wav';
  return new Blob([buffer], { type: mime });
}

function* searchTexts(node: unknown, targetKeys: string[]): Generator<string> {
  if (Array.isArray(node)) {
    for (const item of node) {
      yield* searchTexts(item, targetKeys);
    }
    return;
  }
  if (!node || typeof node !== 'object') return;

  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (targetKeys.includes(key.toLowerCase()) && typeof value === 'string' && value.trim()) {
      yield value.trim();
    } else if (value && typeof value === 'object') {
      yield* searchTexts(value, targetKeys);
    }
  }
}

async function hashLogId(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

async function pushAgentStatusLogs(agents: Record<string, DemCloudTalkAgentLive>): Promise<void> {
  const db = getDbSupabase('dem');
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 19).replace('T', ' ');
  const timeMinute = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

  for (const agent of Object.values(agents)) {
    const oldStatus = lastAgentStatus.get(agent.id) ?? 'offline';
    if (oldStatus === agent.status) continue;

    lastAgentStatus.set(agent.id, agent.status);
    const logId = await hashLogId(`${agent.id}_${agent.status}_${timeMinute}`);

    const { error } = await db.from('cloudtalk_logs').upsert({
      log_id: logId,
      time: timestamp,
      agent: agent.name,
      old_status: oldStatus,
      new_status: agent.status,
    });

    if (error) {
      // Non-fatal — polling should continue even if log write fails.
      console.warn('CloudTalk log upsert failed:', formatSupabaseError(error));
    }
  }
}

function normalizeLiveAgentStatus(raw: string): string {
  const s = raw.trim().toLowerCase().replace(/-/g, '_');
  if (s === 'on_call' || s === 'oncall') return 'on call';
  if (s === 'online' || s === 'idle' || s === 'offline') return s;
  return s || 'offline';
}

function agentDisplayName(agent: Record<string, unknown>, id: string): string {
  // CloudTalk `name` is often an internal/SIP id (e.g. 208707011002) — prefer first+last.
  const first = String(agent.firstname ?? agent.first_name ?? '').trim();
  const last = String(agent.lastname ?? agent.last_name ?? '').trim();
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  const named = String(agent.name ?? '').trim();
  if (named && !/^\d+$/.test(named)) return named;
  return `Agent ${id}`;
}

export async function fetchLiveCloudTalkAgents(
  creds: CloudTalkCredentials,
): Promise<Record<string, DemCloudTalkAgentLive>> {
  const agents: Record<string, DemCloudTalkAgentLive> = {};

  for (let page = 1; page <= 20; page += 1) {
    const response = await cloudTalkFetch(
      creds,
      `/agents/index.json?page=${page}&limit=100`,
      10_000,
    );
    const payload = (await parseCloudTalkJson(response, 'CloudTalk agents API')) as {
      responseData?: { data?: unknown[] };
    };
    const rows = payload.responseData?.data ?? [];
    if (!rows.length) break;

    for (const item of rows) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const nestedAgent = record.Agent as Record<string, unknown> | undefined;
      const nestedUser = record.User as Record<string, unknown> | undefined;
      const agent =
        nestedAgent && Object.keys(nestedAgent).length
          ? nestedAgent
          : nestedUser && Object.keys(nestedUser).length
            ? nestedUser
            : record;

      const id = String(agent.id ?? '');
      if (!id) continue;

      agents[id] = {
        id,
        name: agentDisplayName(agent, id),
        phone: String(agent.default_number ?? agent.phone ?? 'N/A'),
        status: normalizeLiveAgentStatus(
          String(agent.availability_status ?? agent.status ?? 'offline'),
        ),
        callNumberId: String(agent.call_number_id ?? '').trim() || undefined,
      };
    }

    if (rows.length < 100) break;
  }

  return agents;
}

/**
 * One-shot roster sync via Edge Function (names + status + team).
 * Server-side can call CloudTalk /groups and /numbers; the browser proxy cannot.
 */
export async function syncCloudTalkAgentsDirectory(
  _creds?: CloudTalkCredentials | null,
): Promise<number> {
  // Use primary auth client so the session JWT is always attached.
  const { data, error } = await supabase.functions.invoke('cloudtalk-webhook', {
    body: { sync: 'agents' },
  });
  if (error) throw new Error(error.message || 'CloudTalk agent directory sync failed.');
  const result = (data ?? {}) as { ok?: boolean; synced?: number; error?: string };
  if (result.error || result.ok === false) {
    throw new Error(result.error || 'CloudTalk agent directory sync failed.');
  }
  return Number(result.synced ?? 0);
}

export async function syncCloudTalkRecentCalls(creds: CloudTalkCredentials): Promise<void> {
  const response = await cloudTalkFetch(creds, '/calls/index.json?limit=15', 45_000);
  const payload = (await parseCloudTalkJson(response, 'CloudTalk calls API')) as {
    responseData?: { data?: unknown[] };
  };
  const callsData = payload.responseData?.data ?? [];
  const db = getDbSupabase('dem');
  const nowStr = new Date().toISOString().slice(0, 19).replace('T', ' ');

  for (const item of callsData) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const cdr = (row.Cdr as Record<string, unknown> | undefined) ?? {};
    if (!Object.keys(cdr).length) continue;

    const callType = String(cdr.type ?? '').toLowerCase();
    const talkingTime = String(cdr.talking_time ?? '0');
    const externalNum = String(cdr.public_external ?? 'Unknown');
    const callTime = String(cdr.started_at ?? '');
    const callId = String(cdr.id ?? '');
    if (!callId) continue;

    const notesList = [
      ...searchTexts(row.Notes, ['text', 'note', 'content', 'body', 'description', 'message']),
    ];
    const notesStr = [...new Set(notesList)].sort().join(' | ');
    const tagsList = [...searchTexts(row.Tags, ['name', 'title', 'tag_name'])];
    const tagsStr = [...new Set(tagsList)].sort().join(', ');

    if ((notesStr || tagsStr) && externalNum !== 'Unknown' && callTime) {
      const upPayload: Record<string, string> = {};
      if (notesStr) upPayload.remarks = notesStr;
      if (tagsStr) upPayload.tags = tagsStr;
      await db
        .from('missed_calls_tracking')
        .update(upPayload)
        .eq('caller_number', externalNum)
        .gte('call_time', callTime.slice(0, 10));
    }

    if (callType === 'outgoing') {
      const agentName = String((row.Agent as Record<string, unknown> | undefined)?.fullname ?? 'System');
      await db
        .from('missed_calls_tracking')
        .update({
          status: 'Resolved',
          resolved_by: `${agentName} (Callback)`,
          resolved_at: nowStr,
        })
        .eq('caller_number', externalNum)
        .eq('status', 'Pending')
        .lt('call_time', callTime);
      continue;
    }

    if (callType !== 'incoming' || talkingTime !== '0') continue;

    const contact = (row.Contact as Record<string, unknown> | undefined) ?? {};
    const callNumber = (row.CallNumber as Record<string, unknown> | undefined) ?? {};
    const calledLine = String(
      callNumber.internal_name ?? cdr.public_internal ?? 'Unknown',
    );

    const insertPayload = {
      call_id: callId,
      call_time: callTime,
      caller_number: externalNum,
      contact_name: String(contact.name ?? 'Unknown'),
      company_name: String(contact.company ?? 'Individual'),
      called_line: calledLine,
      remarks: notesStr,
      tags: tagsStr,
      status: 'Pending',
    };

    const { error } = await db.from('missed_calls_tracking').insert(insertPayload);
    if (error) {
      const errStr = formatSupabaseError(error).toLowerCase();
      if (errStr.includes('23505') || errStr.includes('duplicate')) {
        const upSpecific: Record<string, string> = {};
        if (notesStr) upSpecific.remarks = notesStr;
        if (tagsStr) upSpecific.tags = tagsStr;
        if (Object.keys(upSpecific).length > 0) {
          await db.from('missed_calls_tracking').update(upSpecific).eq('call_id', callId);
        }
      }
    }
  }
}

export type CloudTalkPollResult = {
  agents: Record<string, DemCloudTalkAgentLive>;
};

export async function pollCloudTalkApi(creds: CloudTalkCredentials): Promise<CloudTalkPollResult> {
  const agents = await fetchLiveCloudTalkAgents(creds);
  await pushAgentStatusLogs(agents);
  await syncCloudTalkRecentCalls(creds);
  return { agents };
}
